import React, { useState, useRef, useEffect } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './ui/command';
import { X, Loader2 } from 'lucide-react';
import { calculateFlight } from '../lib/api';
import { useAirports } from '../contexts/AirportsContext';

export default function FlightFormSidebar({ onFlightAdded}) {
  const { airports } = useAirports();
  const [searchValue, setSearchValue] = useState('');
  const [selectedAirports, setSelectedAirports] = useState([]);
  const [fareClass, setFareClass] = useState('economy');
  const [customMultiplier, setCustomMultiplier] = useState(1.0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const commandRef = useRef(null);

  // Filter airports by IATA code
  const filteredAirports = searchValue.length >= 2
    ? airports.filter(airport =>
        airport.iata_code && airport.iata_code.toUpperCase().startsWith(searchValue.toUpperCase())
      ).slice(0, 8)
    : [];

  const handleSelectAirport = (airport) => {
    setSelectedAirports([...selectedAirports, airport]);
    setSearchValue('');
  };

  const handleRemoveAirport = (index) => {
    setSelectedAirports(selectedAirports.filter((_, i) => i !== index));
  };

  // Handle Enter key - submit form if no dropdown is showing
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && filteredAirports.length === 0 && !searchValue) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (selectedAirports.length < 2) {
      setError('Please select at least 2 airports');
      return;
    }

    setLoading(true);

    try {
      // Create flights for each consecutive pair
      for (let i = 0; i < selectedAirports.length - 1; i++) {
        const origin = selectedAirports[i].iata_code;
        const destination = selectedAirports[i + 1].iata_code;

        // Skip if origin and destination are the same
        if (origin === destination) {
          continue;
        }

        const result = await calculateFlight(
          origin,
          destination,
          fareClass,
          fareClass === 'custom' ? customMultiplier : null
        );
        onFlightAdded(result);
      }

      // Clear form
      setSelectedAirports([]);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 border-b border-border">
      <form onSubmit={handleSubmit} className="space-y-3">
        {/* Airport Search Autocomplete */}
        <div className="relative">
          <Command ref={commandRef} className="rounded-lg border shadow-md" onKeyDown={handleKeyDown}>
            <CommandInput
              placeholder="Search airports..."
              value={searchValue}
              onValueChange={setSearchValue}
            />
            {filteredAirports.length > 0 && (
              <CommandList>
                <CommandGroup>
                  {filteredAirports.map((airport) => (
                    <CommandItem
                      key={airport.iata_code}
                      value={airport.iata_code}
                      onSelect={() => handleSelectAirport(airport)}
                    >
                      <span className="font-semibold">{airport.iata_code}</span>
                      <span className="ml-2 text-muted-foreground text-sm">
                        {airport.name}, {airport.city}
                      </span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            )}
          </Command>
        </div>

        {/* Fare Class Selector */}
        <Select value={fareClass} onValueChange={setFareClass}>
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="basic">Basic Economy (30%)</SelectItem>
            <SelectItem value="economy">Economy (100%)</SelectItem>
            <SelectItem value="first">First Class (200%)</SelectItem>
            <SelectItem value="custom">Custom</SelectItem>
          </SelectContent>
        </Select>

        {/* Custom Multiplier Dropdown - Only shown when Custom is selected */}
        {fareClass === 'custom' && (
          <Select value={customMultiplier.toString()} onValueChange={(val) => setCustomMultiplier(parseFloat(val))}>
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="0.3">30%</SelectItem>
              <SelectItem value="0.5">50%</SelectItem>
              <SelectItem value="1.0">100%</SelectItem>
              <SelectItem value="1.25">125%</SelectItem>
              <SelectItem value="1.5">150%</SelectItem>
              <SelectItem value="1.75">175%</SelectItem>
              <SelectItem value="2.0">200%</SelectItem>
              <SelectItem value="2.5">250%</SelectItem>
              <SelectItem value="3.5">350%</SelectItem>
            </SelectContent>
          </Select>
        )}

        {/* Selected Airport Tags */}
        {selectedAirports.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {selectedAirports.map((airport, index) => (
              <Badge
                key={index}
                variant="default"
                className="px-3 py-1.5 text-sm flex items-center gap-2"
              >
                <span className="font-bold">{airport.iata_code}</span>
                <span className="text-xs opacity-80">
                  {airport.city}
                </span>
                <button
                  type="button"
                  onClick={() => handleRemoveAirport(index)}
                  className="ml-1 hover:opacity-70"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        )}

        {/* Submit Button */}
        <Button
          type="submit"
          disabled={loading || selectedAirports.length < 2}
          className="w-full"
        >
          {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {loading ? 'Adding Flights...' : 'Add Flights'}
        </Button>

        {error && (
          <div className="text-sm text-destructive bg-destructive/10 px-3 py-2 rounded-md">
            {error}
          </div>
        )}
      </form>
    </div>
  );
}
